/** @jsxImportSource ./simboluri-jsx */
import { textVerdict } from "./elemente";
import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { ArmarePerete, ArmarePeretiResult, FemModel, StepState, fmtDate } from "./tipuri";
import Info from "./Info";
import LocalizareCladire from "./LocalizareCladire";
import { incadreaza } from "./incadrare3d";
import TitluVerificare, { useCatalogVerificari } from "./Verificare";
import { Focus } from "./legaturi";
import { DescriereaPasului } from "./TitluPas";
import { CotaH, CotaV, cmFmt } from "./cote";
import Marime from "./Marime";

import NotePas from "./NotePas";
import LameleArmate from "./LameleArmate";
import { t, tn, tx, useT } from "../i18n";
import { numelePunctului, punctScurt } from "./puncte";
import { traduceRezultat } from "./texteRezultat";
// Pasul 14 — Armarea pereților de beton ai subsolului (cutia rigidă).
// Pagina unui panou = «nota de calcul» completă, la același nivel de detaliu
// ca la grinzi (Pasul 13): 3D cu armătura, desenele 2D (secțiune prin
// grosime, secțiune verticală, elevație), eforturile din cele 5 puncte,
// fiecare verificare cu formula și articolul ei — plus lista EXPLICITĂ a
// verificărilor de la grinzi care NU se aplică, cu articolul care scutește.

const PUNCTE = ["R1_sus", "R1_jos", "camp", "camp_V", "R2_sus", "R2_jos"] as const;

// culorile sunt aceleași în 3D, în desenele 2D și în legende — altfel
// desenele nu se pot citi unul prin celălalt
const COL = {
  vertical: "#b5323d",
  orizontal: "#2e7d72",
  tirant: "#a97614",
  agrafe: "#6b4fa1",
  beton: "#ececec",
  betonLinie: "#858585",
  stalp: "#f7e2e4",
  stalpLinie: "#b5323d",
  placa: "#dedede",
  placaLinie: "#9f9f9f",
  rost: "#b8730c",
};

function nr(v: number | undefined | null, zecimale = 1): string {
  if (v === undefined || v === null) return "—";
  return v.toFixed(zecimale).replace(".", ",");
}

function verdictClass(v: string): string {
  if (v.startsWith("SE VERIFICA")) return "st-ok";
  if (v.startsWith("ATENTIE")) return "st-warn";
  if (v.startsWith("nefisurat")) return "st-ok";
  return "st-bad";
}

function Verdict({ v, marja }: { v: string; marja?: number | null }) {
  return (
    <>
      <span className={verdictClass(v)}>{textVerdict(v)}</span>
      {marja ? (
        <span
          className="st-warn"
          style={{ marginLeft: 4 }}
          title={t("struct.arp.r_sub_1", { p1: marja })}
        >
          {t("struct.arp.prin_marja", { marja })}
        </span>
      ) : null}
    </>
  );
}

function modelFem(st: StepState | null | undefined): FemModel | null {
  const r = st?.result as unknown as { model?: FemModel } | undefined;
  return r?.model ?? null;
}

// ---------------------------------------------------------------------------
// Secțiune de „notă de calcul" — card cu explicație + formule cu valori
// ---------------------------------------------------------------------------

function Sectiune({
  titlu,
  explicatie,
  formule,
  termeni,
  children,
}: {
  titlu: string;
  explicatie: string;
  formule?: { eticheta: string; valori: string; ref: string }[];
  termeni?: [string, string][];
  children?: React.ReactNode;
}) {
  useT();
  return (
    <div className="st-findings">
      <h3>{titlu}</h3>
      <div className="sg-muted" style={{ marginBottom: 8, whiteSpace: "pre-line" }}>
        {explicatie}
      </div>
      {termeni && (
        <details style={{ marginBottom: 8 }}>
          <summary className="sg-muted" style={{ cursor: "pointer" }}>
            {t("struct.arp.termenii_folositi_romane")}
          </summary>
          <ul className="sg-muted" style={{ marginTop: 4 }}>
            {termeni.map(([t, d], i) => (
              <li key={i}>
                <strong>{t}</strong> — {d}
              </li>
            ))}
          </ul>
        </details>
      )}
      {formule && (
        <table className="sg-table" style={{ marginBottom: 8 }}>
          <tbody>
            {formule.map((f, i) => (
              <tr key={i}>
                <td>
                  {f.eticheta}
                  <div className="sg-muted" style={{ fontSize: 11 }}>{f.ref}</div>
                </td>
                <td className="sg-mono" style={{ fontSize: 13 }}>{f.valori}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------

export default function StepArmarePereti({
  state,
  femState,
  running,
  focus,
  projectId,
  solutie,
}: {
  state: StepState | null;
  femState?: StepState | null;
  running: boolean;
  // panoul cerut dintr-un link (?focus=PB3) — v. legaturi.ts
  focus?: Focus;
  // proiectul + soluția vizualizată: viewerul «unde e în clădire» le folosește
  // ca să poată sări la grinda sau la stâlpul pe care dai click
  projectId?: string;
  solutie?: string | null;
}) {
  useT();
  const model = modelFem(femState);

  if (running) {
    return (
      <div className="st-status">
        <div className="st-spinner" />
        <div>
          <strong>{t("struct.arp.pasul_ruleaza")}</strong>
          <div className="sg-muted">
            {t("struct.arp.armeaza_peretii_beton")}
          </div>
        </div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="sg-muted st-empty">
        {t("struct.arp.pasul_nu_rulat")}
{/* descrierea vine din catalog_pasi.py — același text cu
            cel de la hover pe titlul pasului */}
        <DescriereaPasului cheie="armare_pereti" />
      </div>
    );
  }

  const r = state.result as unknown as ArmarePeretiResult;

  if (state.status === "error") {
    return (
      <div>
        <div className="sg-error">
          {t("struct.arp.ultima_rulare_esuat", {
            motiv: r?.error ?? t("struct.arp.eroare_necunoscuta"),
          })}
        </div>
        <div className="sg-muted">{t("struct.arp.ruleaza_nou_meniul")}</div>
      </div>
    );
  }

  // lamelele (pereții structurali ai clădirii) au secțiunea lor, oricum ar
  // sta panourile de subsol: `activ` e numai al panourilor
  const areLamele = (r.lamele?.length ?? 0) > 0;
  if (!r.activ && areLamele) {
    return (
      <div className="st-result">
        {r.cerut ? (
          <div className="st-findings">
            <h3>{t("struct.arp.subsol_cerut_fara_perete")}</h3>
            {(r.avertismente ?? []).map((a, i) => (
              <div key={i} className="st-bad">• {a}</div>
            ))}
          </div>
        ) : (
          <div className="sg-muted" style={{ marginBottom: 8 }}>
            {t("struct.arl.fara_panouri")}
          </div>
        )}
        <LameleArmate r={r} focus={focus} />
        <div className="sg-muted st-ran-at">{t("struct.arp.rulat_la", { data: fmtDate(state.updated_at) })}</div>
      </div>
    );
  }

  if (!r.activ) {
    // clădirea CERE subsol rigid, dar n-a ieșit niciun panou: e o problemă
    // de geometrie, nu o normalitate — se spune ca atare, nu ca „inactiv"
    if (r.cerut) {
      return (
        <div className="st-result">
          <div className="st-findings">
            <h3>{t("struct.arp.subsol_cerut_fara_perete")}</h3>
            {(r.avertismente ?? []).map((a, i) => (
              <div key={i} className="st-bad">• {a}</div>
            ))}
            <div className="sg-muted" style={{ marginTop: 8 }}>
              {t("struct.arp.panourile_nasc_pasul")}
            </div>
          </div>
          <div className="sg-muted st-ran-at">{t("struct.arp.rulat_la", { data: fmtDate(state.updated_at) })}</div>
        </div>
      );
    }
    return (
      <div className="st-result">
        <div className="st-findings">
          <h3>{t("struct.arp.pas_inactiv_proiect")}</h3>
          <div className="sg-muted">
            {r.motiv ?? t("struct.arp.nu_cere_subsol_rigid")}{" "}
            {t("struct.arp.peretii_doar_p2")}
          </div>
        </div>
        <div className="sg-muted st-ran-at">{t("struct.arp.rulat_la", { data: fmtDate(state.updated_at) })}</div>
      </div>
    );
  }

  return (
    <>
      <PeretiView r={r} model={model} updatedAt={state.updated_at} focus={focus}
                  projectId={projectId} solutie={solutie} />
      {areLamele && (
        <div className="st-result">
          <LameleArmate r={r} focus={focus} />
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

function PeretiView({
  r,
  model,
  updatedAt,
  focus,
  projectId,
  solutie,
}: {
  r: ArmarePeretiResult;
  model: FemModel | null;
  updatedAt: string;
  focus?: Focus;
  projectId?: string;
  solutie?: string | null;
}) {
  useT();
  const [sel, setSel] = useState<string>("");
  const pereti = useMemo(
    () =>
      [...r.pereti].sort((a, b) =>
        a.element.localeCompare(b.element, undefined, { numeric: true })
      ),
    [r]
  );
  const cheie = (p: ArmarePerete) => `${p.element}|${p.nivel}`;
  // linkul poartă id-ul de plan al panoului („PB3"), iar rândul e cheiat pe
  // «element|nivel»: referința se REZOLVĂ în lista pasului, altfel selecția
  // nu se potrivește și linkul pare mort, fără nicio eroare
  useEffect(() => {
    if (!focus?.ref) return;
    const gasit = pereti.find((x) => x.element === focus.ref);
    if (gasit) setSel(cheie(gasit));
  }, [focus?.n, focus?.ref, pereti]);
  const p = pereti.find((x) => cheie(x) === sel) ?? pereti[0];
  const sr = r.subsol_rigid;
  const trece = sr?.trece === true;
  const par = r.parametri;

  return (
    <div className="st-result">
      <div className="st-cards">
        <div className="st-card st-card-main">
          <div className="st-card-label">{t("struct.arp.pereti_armati")}<Marime cheie="pereti_armati" /></div>
          <div className="st-card-value">{r.nr_pereti}</div>
          <div className="st-card-sub">
            {Object.entries(r.verdicte)
              .filter(([, n]) => n > 0)
              .map(([v, n]) => `${n} ${v}`)
              .join(" · ") || "—"}
          </div>
        </div>
        <div className="st-card">
          <div className="st-card-label">
            {t("struct.arp.regimul_sectiunii")}
            <Info
              titlu={t("struct.arp.grinda_grinda_perete")}
              ce={t("struct.arp.sr_en_1992")}
              deCe={t("struct.arp.panourile_subsol_au")}
            />
          </div>
          <div className="st-card-value">
            {r.nr_grinzi_perete ?? 0} / {r.nr_pereti}
          </div>
          <div className="st-card-sub">
            {t("struct.arp.panouri_grinzi_pereti", {
              raport: nr(par?.raport_grinda, 0),
            })}
          </div>
        </div>
        {sr?.raport_x != null && (
          <div className="st-card">
            <div className="st-card-label">
              {t("struct.arp.subsol_rigid")}
              <Info
                titlu={t("struct.arp.criteriul_subsolului")}
                ce={t("struct.arp.rigiditatea_laterala_a")}
                deCe={t("struct.arp.criteriul_practica_struc")}
              />
            </div>
            <div className="st-card-value">
              <span className={trece ? "st-ok" : "st-bad"}>
                {trece ? "✓" : "✗"} {nr(sr.raport_x, 2)}× / {nr(sr.raport_y, 2)}×
              </span>
            </div>
            <div className="st-card-sub">
              {t("struct.arp.k_subsol_sub", { prag: nr(sr.prag, 1) })}
            </div>
          </div>
        )}
        <div className="st-card">
          <div className="st-card-label">{t("struct.arp.materiale")}</div>
          <div className="st-card-value">{nr(r.otel_total_kg, 0)} kg</div>
          <div className="st-card-sub">
            {t("struct.arp.materiale_sub", {
              otel: par?.otel, beton: nr(r.beton_total_m3, 1), clasa: par?.beton,
              fcd: par?.f_cd_mpa
                ? t("struct.arp.materiale_fcd", {
                    fcd: nr(par.f_cd_mpa, 2), fctd: nr(par.f_ctd_mpa, 2),
                  })
                : "",
            })}
          </div>
        </div>
      </div>

      <NotePas note={r.avertismente} titlu="struct.note.panouri" />

      <div className="st-findings">
        <h3>{t("struct.arp.panourile")}</h3>
        <div style={{ overflowX: "auto" }}>
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("struct.arp.th_panou")}</th>
                <th>{t("struct.arp.intre_stalpii")}</th>
                <th>L × h × t [m]</th>
                <th title={t("struct.arp.deschiderea_calcul_inalt")}>l₀/h</th>
                <th>
                  {t("struct.arp.plasa_verticala")}
                  <Info
                    titlu={t("struct.arp.barele_verticale")}
                    ce={t("struct.arp.montate_inaltimea_subsol")}
                    deCe={t("struct.arp.minim_10_distanta")}
                  />
                </th>
                <th>
                  {t("struct.arp.plasa_orizontala")}
                  <Info
                    titlu={t("struct.arp.barele_orizontale_repart")}
                    ce={t("struct.arp.plasa_ortogonala_ambele")}
                    deCe={t("struct.arp.minim_8_distanta")}
                  />
                </th>
                <th>
                  {t("struct.arp.tiranti_sus_jos")}
                  <Info
                    titlu={t("struct.arp.tirantii_banda_intinsa")}
                    ce={t("struct.arp.bare_orizontale_concentr")}
                    deCe={t("struct.arp.grinda_perete_deformatii")}
                  />
                </th>
                <th>{t("struct.arp.th_agrafe")}</th>
                <th>{t("struct.arp.otel_kg")}</th>
                <th>{t("struct.arp.th_verdict")}</th>
              </tr>
            </thead>
            <tbody>
              {pereti.map((x) => {
                const v = x.armare.vertical;
                const o = x.armare.orizontal;
                const a = x.armare.agrafe;
                const ts = x.armare.tirant_sus;
                const tj = x.armare.tirant_jos;
                const activ = cheie(x) === cheie(p);
                return (
                  <tr
                    key={cheie(x)}
                    onClick={() => setSel(cheie(x))}
                    style={{
                      cursor: "pointer",
                      background: activ ? "var(--sg-bg-alt, #e2f4f7)" : undefined,
                    }}
                  >
                    <td>
                      <strong>{x.element}</strong>{" "}
                      <span className="sg-muted">
                        ({x.nivel}, {t(x.perimetral ? "struct.arp.contur" : "struct.rzv.interior")})
                      </span>
                    </td>
                    <td>{x.stalp_i ?? "?"} – {x.stalp_j ?? "?"}</td>
                    <td>
                      {nr(x.L_ax_m, 2)} × {nr(x.h_cm / 100, 2)} × {nr(x.t_cm / 100, 2)}
                    </td>
                    <td className="sg-mono">
                      {nr(x.regim?.l0_h, 2)}
                      {x.regim?.grinda_perete ? (
                        <span className="sg-muted" title={t("struct.arp.grinda_perete_sr")}> GP</span>
                      ) : null}
                    </td>
                    <td>
                      Ø{v.phi_mm}/{v.pas_mm}{" "}
                      <span className="sg-muted">(ρ {nr(v.rho_pct, 2)}%)</span>
                    </td>
                    <td>
                      Ø{o.phi_mm}/{o.pas_mm}{" "}
                      <span className="sg-muted">(ρ {nr(o.rho_pct, 2)}%)</span>
                    </td>
                    <td>
                      {ts.n || tj.n ? (
                        <>
                          {ts.n ? `${ts.n}Ø${ts.phi_mm}` : "—"} /{" "}
                          {tj.n ? `${tj.n}Ø${tj.phi_mm}` : "—"}
                        </>
                      ) : (
                        <span className="sg-muted">{t("struct.arp.plasa_banda_ajunge")}</span>
                      )}
                    </td>
                    <td>
                      {a.per_m2}Ø{a.phi_mm}/m²{" "}
                      <span className="sg-muted">({a.nr} buc)</span>
                    </td>
                    <td>{nr(x.otel_kg, 0)}</td>
                    <td><span className={verdictClass(x.verdict)}>{textVerdict(x.verdict)}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="sg-muted" style={{ marginTop: 6 }}>
          {t("struct.arp.click_rand_deschide")}
        </div>
      </div>

      {p && (
        <div className="st-findings">
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <label className="sg-muted">
              {t("struct.arp.panoul_eticheta")}{" "}
              <select value={cheie(p)} onChange={(e) => setSel(e.target.value)}>
                {pereti.map((x) => (
                  <option key={cheie(x)} value={cheie(x)}>
                    {t("struct.arp.optiune_panou", {
                      el: x.element, nivel: x.nivel, verdict: x.verdict,
                    })}
                  </option>
                ))}
              </select>
            </label>
            <span className={verdictClass(p.verdict)}>{textVerdict(p.verdict)}</span>
            <span className="sg-muted">
              {t("struct.arp.panou_dimensiuni", {
                t: nr(p.t_cm, 0), h: nr(p.h_cm, 0), l: nr(p.L_ax_m, 2),
                l0: nr(p.l0_m, 2), si: p.stalp_i ?? "?", sj: p.stalp_j ?? "?",
              })}
              {p.off_i_m || p.off_j_m ? (
                <span title={t("struct.arp.portiunile_panou_interio")}>
                  {t("struct.arp.rigid_off", {
                    i: nr((p.off_i_m ?? 0) * 100, 0),
                    j: nr((p.off_j_m ?? 0) * 100, 0),
                  })}
                </span>
              ) : null}
            </span>
          </div>
          <div className="sg-muted" style={{ marginTop: 6, lineHeight: 1.55 }}>
            <strong>{t("struct.arp.inseamna_verdictul")}</strong>{" "}
            {t("struct.arp.verdictul_aduna")}{" "}
            {Number(par?.acceptare_grinzi_pct ?? 0) > 0 && (
              <>
                <strong>{t("struct.arp.marja_subdimensionare_ac")}</strong>{" "}
                {t("struct.arp.marja_proiect", {
                  prag: String(par?.prag_re_acceptare),
                  pct: String(par?.acceptare_grinzi_pct),
                })}
              </>
            )}
          </div>
        </div>
      )}

      {p && (
        <NotaCalculPerete
          p={p}
          r={r}
          model={model}
          projectId={projectId}
          solutie={solutie}
          // click pe ALT panou în viewer = se schimbă selecția aici, fără să
          // se reîncarce pagina (și fără să sară camera)
          pePerete={(ref) => {
            const g = pereti.find((x) => x.element === ref);
            if (g) setSel(cheie(g));
          }}
        />
      )}

      {(r.nu_se_aplica?.length ?? 0) > 0 && (
        <div className="st-findings">
          <h3>
            {t("struct.arp.ce_nu_se_preia")}
            <Info
              titlu={t("struct.arp.de_ce_lista_asta")}
              ce={t("struct.arp.peretele_foarte_asemanat")}
              deCe={t("struct.arp.scutirile_pornesc_acelas")}
            />
          </h3>
          <div style={{ overflowX: "auto" }}>
            <table className="sg-table">
              <thead>
                <tr>
                  <th>{t("struct.arp.verificarea_grinzi")}</th>
                  <th>{t("struct.arp.unde_grinzi")}</th>
                  <th>{t("struct.arp.aplica")}</th>
                  <th>{t("struct.arp.th_articolul")}</th>
                </tr>
              </thead>
              <tbody>
                {r.nu_se_aplica!.map((x, i) => (
                  <tr key={i}>
                    <td><strong>{x.nume}</strong></td>
                    <td className="sg-muted">{x.la_grinzi}</td>
                    <td style={{ maxWidth: 520 }}>{x.motiv}</td>
                    <td className="sg-muted" style={{ fontSize: 12 }}>{x.referinta}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(r.recomandari_cutie_rigida?.length ?? 0) > 0 && (
        <div className="st-findings">
          <h3>
            {t("struct.arp.ce_cere_cutia")}
            <Info
              titlu={t("struct.arp.cutia_rigida_a")}
              ce={t("struct.arp.peretii_dintre_stalpi")}
              deCe={t("struct.arp.p100_1_4")}
            />
          </h3>
          <ul className="sg-muted" style={{ lineHeight: 1.6, maxWidth: 900 }}>
            {r.recomandari_cutie_rigida!.map((txt, i) => (
              <li key={i}>{txt}</li>
            ))}
          </ul>
        </div>
      )}

      {(par?.referinte?.length ?? 0) > 0 && (
        <div className="st-findings">
          <h3>{t("struct.arp.baza_normativa")}</h3>
          <ul className="sg-muted" style={{ lineHeight: 1.6, maxWidth: 900 }}>
            {par!.referinte!.map((txt, i) => (
              <li key={i}>{txt}</li>
            ))}
          </ul>
          <div className="sg-muted" style={{ fontSize: 12, marginTop: 6, maxWidth: 900 }}>
            {tx("struct.arp.ierarhia_normativ", {
              acop: nr(par?.acoperire_cm),
              ampl: nr(par?.amplificare_infrastructura),
              wmax: nr(par?.fisura_wmax_mm, 1),
            })}
            {par?.nota_editie_cr2 ? <> {par.nota_editie_cr2}</> : null}
          </div>
        </div>
      )}

      <div className="sg-muted st-ran-at">{t("struct.arp.rulat_la", { data: fmtDate(updatedAt) })}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Nota de calcul a unui panou
// ---------------------------------------------------------------------------

/** Nota de calcul a unui perete. `faraVederi` lasă deoparte scena 3D și
 *  localizarea din capul ei: pagina elementului le are deja, deasupra. */
export function NotaCalculPerete({
  p,
  r,
  model,
  projectId,
  solutie,
  pePerete,
  faraVederi,
}: {
  p: ArmarePerete;
  r: ArmarePeretiResult;
  model: FemModel | null;
  projectId?: string;
  solutie?: string | null;
  pePerete?: (ref: string) => void;
  faraVederi?: boolean;
}) {
  useT();
  const par = r.parametri;
  const reg = p.regim;
  const a = p.armare;
  // aria din bandă și forța ei, folosite în exemplul de calcul al tirantului
  const asBanda = (a.As_plasa_banda_cm2 ?? 0)
    + Math.max(a.tirant_sus.As_cm2, a.tirant_jos.As_cm2);
  const tRd = (asBanda * (r.parametri?.f_yd_mpa ?? 435)) / 10;
  const gp = reg?.grinda_perete;
  // titlurile verificărilor vin din catalogul serverului, ca la Pașii 13, 15
  // și 16; numele emis de pas rămâne textul vizibil (aici e mai precis decât
  // eticheta categoriei), iar explicația completă stă la hover
  const catalog = useCatalogVerificari();
  const verif = (prefix: string) =>
    p.verificari.filter((v) => v.nume.startsWith(prefix));

  return (
    <>
      {/* --- 3D + unde e în clădire ---------------------------------------- */}
      {!faraVederi && (
      <div className="st-findings" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 480px", minWidth: 0 }}>
            <Perete3D p={p} />
          </div>
          <div
            style={{
              flex: "0 0 340px",
              maxWidth: "100%",
              borderLeft: "1px solid #dedede",
            }}
          >
            <LocalizareCladire
              model={model}
              evidentiate={[p.element]}
              height={380}
              projectId={projectId}
              solutie={solutie}
              pasCurent="armare_pereti"
              peElement={pePerete}
            />
          </div>
        </div>
        <div className="sg-muted" style={{ padding: "6px 12px", fontSize: 12, lineHeight: 1.6 }}>
          {tx("struct.arp.legenda_3d", {
            h: nr(p.h_cm, 0), t: nr(p.t_cm, 0), acop: nr(par?.acoperire_cm, 1),
          })}
        </div>
      </div>
      )}

      {/* --- 1. Geometria și regimul secțiunii ----------------------------- */}
      <Sectiune
        titlu={t("struct.arp.1_geometria_regimul")}
        explicatie={t("struct.arp.s1_explicatie", {
          el: p.element, t: nr(p.t_cm, 0), h: nr(p.h_cm, 0),
          si: p.stalp_i ?? "?", sj: p.stalp_j ?? "?",
          lax: nr(p.L_ax_m, 2), oi: nr(p.off_i_m ?? 0, 2),
          oj: nr(p.off_j_m ?? 0, 2), l0: nr(p.l0_m, 2),
          raport: nr(reg?.l0_h, 2),
          regim: t(gp ? "struct.arp.este_grinda_perete"
                     : "struct.arp.e_grinda_obisnuita"),
          urmare: gp
            ? t("struct.arp.conteaza_grinda_zvelta", {
                p1: nr(reg?.z_bernoulli_m, 2),
                p2: nr(reg?.z_grinda_perete_m, 2),
                p3: nr(reg?.banda_tirant_cm, 0),
              })
            : t("struct.arp.panou_zvelt"),
        })}
        formule={
          gp
            ? [
                {
                  eticheta: t("struct.arp.raportul_decide_regimul"),
                  valori: t("struct.arp.valori_l0h", {
                    l0: nr(p.l0_m, 2), h: nr(p.h_cm / 100, 2),
                    raport: nr(reg?.l0_h, 2), prag: nr(reg?.prag, 0),
                  }),
                  ref: "SR EN 1992-1-1 §5.3.1(3)",
                },
                {
                  eticheta: t("struct.arp.bratul_parghie_grinzii"),
                  valori: t("struct.arp.valori_z", {
                    l0: nr(p.l0_m, 2), h2: nr(2 * (p.h_cm / 100), 2),
                    z: nr(reg?.z_grinda_perete_m, 2), zb: nr(reg?.z_bernoulli_m, 2),
                  }),
                  ref: t("struct.arp.ref_conventia_grinzi_pereti"),
                },
                {
                  eticheta: t("struct.arp.banda_intinsa_aduna"),
                  valori: t("struct.arp.valori_banda", {
                    frac: nr((reg?.banda_tirant_frac ?? 0.2) * 100, 0),
                    cm: nr(reg?.banda_tirant_cm, 0),
                    inb: a.orizontal.nr_randuri_banda ?? 0,
                    tot: a.orizontal.nr_randuri ?? 0,
                    as: nr(a.As_plasa_banda_cm2, 2),
                  }),
                  ref: t("struct.arp.ref_tirantul_ancorat"),
                },
              ]
            : undefined
        }
        termeni={[
          [t("struct.arp.termen_grinda_perete"), t("struct.arp.termen_grinda_perete_def")],
          [t("struct.arp.termen_tirant"), t("struct.arp.termen_tirant_def")],
          [t("struct.arp.termen_brat"), t("struct.arp.termen_brat_def")],
          [t("struct.arp.termen_zona_rigida"), t("struct.arp.termen_zona_rigida_def")],
        ]}
      >
        {gp && reg?.nota_convenție && (
          <div className="st-warn" style={{ marginTop: 6 }}>
            • {reg.nota_convenție}.
          </div>
        )}
        {(p.note?.length ?? 0) > 0 && (
          <div style={{ marginTop: 8 }}>
            {p.note!.map((n, i) => (
              <div key={i} className="st-warn">• {n}</div>
            ))}
          </div>
        )}
      </Sectiune>

      {/* --- 2. Eforturile de calcul --------------------------------------- */}
      <Sectiune
        titlu={t("struct.arp.s2_titlu")}
        explicatie={t("struct.arp.s2_explicatie")}
      >
        <div style={{ overflowX: "auto" }}>
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("struct.arp.th_punct")}</th>
                <th>{t("struct.arp.th_grupare")}</th>
                <th title={t("struct.arp.pozitia_statiei_axul")}>x (m)</th>
                <th title={t("struct.arp.momentul_calcul_planul")}>M₃ (kN·m)</th>
                <th title={t("struct.arp.fata_intinsa_rezultata")}>{t("struct.arp.fata_intinsa")}</th>
                <th>V₂ (kN)</th>
                <th title={t("struct.arp.negativ_compresiune")}>P (kN)</th>
                <th title={t("struct.arp.momentul_combinatia_cvas")}>M₃,GS</th>
                <th title={t("struct.arp.combinatia_da_extremul")}>{t("struct.arp.combinatia")}</th>
              </tr>
            </thead>
            <tbody>
              {(["fundamentala", "seismica"] as const).flatMap((gr) =>
                PUNCTE.map((pct) => {
                  const x = p.puncte.find(
                    (q) => q.grupa === gr && q.punct === pct
                  );
                  if (!x) return null;
                  return (
                    <tr key={`${gr}-${pct}`}>
                      <td className="sg-mono" title={numelePunctului(pct)}>
                        {punctScurt(pct)}
                      </td>
                      <td className="sg-muted">
                        {gr}
                        {x.amplificat ? t("struct.arp.amplificat_omega") : ""}
                      </td>
                      <td className="sg-mono">{nr(x.loc_m, 2)}</td>
                      <td className="sg-mono"><strong>{nr(x.M3_knm)}</strong></td>
                      <td
                        className="sg-mono"
                        style={{ color: x.M3_knm < 0 ? COL.vertical : COL.orizontal }}
                      >
                        {t(x.M3_knm < 0 ? "struct.arp.fata_sus" : "struct.arp.fata_jos")}
                      </td>
                      <td className="sg-mono">{nr(x.V2_kn)}</td>
                      <td className="sg-mono">{nr(x.P_kn)}</td>
                      <td className="sg-mono">{nr(x.M3_gs_knm)}</td>
                      <td className="sg-mono sg-muted">{x.combinatie}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Sectiune>

      {/* --- 3. Amplificarea infrastructurii ------------------------------- */}
      <Sectiune
        titlu={t("struct.arp.3_combinatia_dimensionea")}
        explicatie={t("struct.arp.s3_explicatie", {
          ampl: nr(par?.amplificare_infrastructura),
        })}
      />

      {/* --- 4. Armarea aleasă + desenele 2D ------------------------------ */}
      <Sectiune
        titlu={t("struct.arp.4_armarea_aleasa")}
        explicatie={t("struct.arp.s4_explicatie", {
          rho: nr(par?.rho_min_pct, 2),
          incercari: p.nr_incercari != null
            ? t("struct.arp.s4_incercari", {
                n: tn("struct.arp.incercari#n", p.nr_incercari),
              })
            : "",
        })}
      >
        <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "flex-start" }}>
          <SectiunePereteGrosime p={p} acoperire={par?.acoperire_cm ?? 3.5} />
          <div style={{ minWidth: 300 }}>
            <SectiuneVerticalaPerete p={p} acoperire={par?.acoperire_cm ?? 3.5} />
            <div className="sg-muted" style={{ fontSize: 12, lineHeight: 1.5, maxWidth: 340 }}>
              <span style={{ color: COL.orizontal }}>
                {t("struct.arp.legenda_randuri_oriz", {
                  n: a.orizontal.nr_randuri ?? "?",
                  phi: a.orizontal.phi_mm, pas: a.orizontal.pas_mm,
                })}
              </span>
              {" · "}
              <span style={{ color: COL.vertical }}>
                {t("struct.arp.legenda_bare_vert", {
                  phi: a.vertical.phi_mm, pas: a.vertical.pas_mm,
                })}
              </span>
              {gp && (
                <>
                  {" · "}
                  <span style={{ color: COL.tirant }}>
                    {t("struct.arp.legenda_tirant", {
                      sus: a.tirant_sus.n
                        ? `${a.tirant_sus.n}Ø${a.tirant_sus.phi_mm}`
                        : t("struct.arp.numai_plasa_banda"),
                      jos: a.tirant_jos.n
                        ? `${a.tirant_jos.n}Ø${a.tirant_jos.phi_mm}`
                        : t("struct.arp.numai_plasa_banda"),
                      banda: nr(reg?.banda_tirant_cm, 0),
                    })}
                  </span>
                </>
              )}
              {" · "}
              <span style={{ color: COL.rost }}>
                {t("struct.arp.legenda_rosturi")}
              </span>
            </div>
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <ElevatiePerete p={p} />
          <div className="sg-muted" style={{ fontSize: 12, lineHeight: 1.5, maxWidth: 900 }}>
            <span style={{ color: COL.vertical }}>
              {t("struct.arp.legenda_elev_vert", {
                phi: a.vertical.phi_mm, pas: a.vertical.pas_mm,
              })}
            </span>
            {" · "}
            <span style={{ color: COL.orizontal }}>
              {t("struct.arp.legenda_elev_oriz", {
                phi: a.orizontal.phi_mm, pas: a.orizontal.pas_mm,
              })}
            </span>
            {" · "}
            {tx("struct.arp.legenda_elevatie", {
              otel: nr(p.otel_kg, 0), beton: nr(p.beton_m3, 2),
            })}
          </div>
        </div>
        <div style={{ marginTop: 12, minWidth: 280 }}>
          <table className="sg-table">
            <tbody>
              <tr>
                <td>{t("struct.arp.plasa_verticala_ambele")}</td>
                <td className="sg-mono">
                  {t("struct.arp.val_plasa", {
                    phi: a.vertical.phi_mm, pas: a.vertical.pas_mm,
                    n: a.vertical.nr_bare, rho: nr(a.vertical.rho_pct, 3),
                  })}
                </td>
              </tr>
              <tr>
                <td>{t("struct.arp.plasa_orizontala_ambele")}</td>
                <td className="sg-mono">
                  {t("struct.arp.val_plasa_randuri", {
                    phi: a.orizontal.phi_mm, pas: a.orizontal.pas_mm,
                    n: a.orizontal.nr_randuri ?? "?",
                    rho: nr(a.orizontal.rho_pct, 3),
                  })}
                </td>
              </tr>
              {gp && (
                <tr>
                  <td>{t("struct.arp.tirant_sus_banda")}</td>
                  <td className="sg-mono">
                    {a.tirant_sus.n
                      ? t("struct.arp.val_tirant", {
                          n: a.tirant_sus.n, phi: a.tirant_sus.phi_mm,
                          as: nr(a.tirant_sus.As_cm2, 2),
                          plasa: nr(a.As_plasa_banda_cm2, 2),
                        })
                      : t("struct.arp.val_tirant_doar_plasa", {
                          plasa: nr(a.As_plasa_banda_cm2, 2),
                        })}
                  </td>
                </tr>
              )}
              {gp && (
                <tr>
                  <td>{t("struct.arp.tirant_jos_banda")}</td>
                  <td className="sg-mono">
                    {a.tirant_jos.n
                      ? t("struct.arp.val_tirant", {
                          n: a.tirant_jos.n, phi: a.tirant_jos.phi_mm,
                          as: nr(a.tirant_jos.As_cm2, 2),
                          plasa: nr(a.As_plasa_banda_cm2, 2),
                        })
                      : t("struct.arp.val_tirant_doar_plasa", {
                          plasa: nr(a.As_plasa_banda_cm2, 2),
                        })}
                  </td>
                </tr>
              )}
              <tr>
                <td>{t("struct.arp.agrafe_legare")}</td>
                <td className="sg-mono">
                  {t("struct.arp.val_agrafe", {
                    n: a.agrafe.per_m2, phi: a.agrafe.phi_mm, buc: a.agrafe.nr,
                    carlig: a.agrafe.carlig
                      ? t("struct.arp.val_agrafe_carlig", { c: a.agrafe.carlig })
                      : "",
                  })}
                </td>
              </tr>
              <tr>
                <td>{t("struct.arp.materiale")}</td>
                <td className="sg-mono">
                  {t("struct.arp.val_materiale", {
                    otel: nr(p.otel_kg, 0), beton: nr(p.beton_m3, 2),
                    rap: nr(p.otel_kg / Math.max(p.beton_m3, 0.01), 0),
                  })}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Sectiune>

      {/* --- 5. Încovoierea în plan: două modele -------------------------- */}
      <Sectiune
        titlu={t("struct.arp.5_incovoierea_plan")}
        explicatie={t("struct.arp.s5_explicatie", {
          urmare: gp
            ? t("struct.arp.modelul_grinda_zabrele", {
                p1: nr(reg?.banda_tirant_cm, 0),
                p2: nr(reg?.z_grinda_perete_m, 2),
              })
            : t("struct.arp.panou_zvelt_5"),
        })}
      >
        <div style={{ overflowX: "auto" }}>
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("struct.arp.th_verificarea")}</th>
                <th>{t("struct.arp.th_punct")}</th>
                <th>{t("struct.arp.th_grupare")}</th>
                <th title={t("struct.arp.momentul_de_calcul")}>E = M_Ed (kN·m)</th>
                <th title={t("struct.arp.momentul_capabil")}>R = M_Rd (kN·m)</th>
                <th>R/E</th>
                <th>{t("struct.arp.th_verdict")}</th>
              </tr>
            </thead>
            <tbody>
              {[...verif("M+N în plan"), ...verif("M în plan — grindă cu zăbrele")].map(
                (v, i) => (
                  <tr key={i}>
                    <td>
                      <TitluVerificare catalog={catalog} pas="armare_pereti"
                                       nume={v.nume} grupa={v.grupa}
                                       text={v.nume} aratNumele={false}
                                       aratPasul={false} />
                      {v.referinta && (
                        <div className="sg-muted" style={{ fontSize: 11 }}>{v.referinta}</div>
                      )}
                    </td>
                    <td className="sg-mono" title={numelePunctului(v.punct)}>
                      {punctScurt(v.punct)}
                    </td>
                    <td className="sg-muted">{v.grupa}</td>
                    <td className="sg-mono">{typeof v.E === "number" ? nr(v.E) : v.E}</td>
                    <td className="sg-mono"><strong>{typeof v.R === "number" ? nr(v.R) : v.R}</strong></td>
                    <td className="sg-mono">{v.RE == null ? "—" : nr(v.RE, 2)}</td>
                    <td><Verdict v={v.verdict} marja={v.acceptat_marja} /></td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
        {gp && (
          <div className="sg-muted" style={{ marginTop: 8, fontSize: 13 }}>
            {t("struct.arp.exemplu_tirant", {
              as: nr(asBanda, 2), fyd: par?.f_yd_mpa, trd: nr(tRd, 0),
              mrd: nr(tRd * (reg?.z_grinda_perete_m ?? 0), 0),
            })}
          </div>
        )}
      </Sectiune>

      {/* --- 6. Tăietoarea în plan ---------------------------------------- */}
      <Sectiune
        titlu={t("struct.arp.6_taietoarea_plan")}
        explicatie={t("struct.arp.s6_explicatie", {
          z: nr(gp ? reg?.z_grinda_perete_m : reg?.z_bernoulli_m, 2),
          vrdc: nr(p.v_rd_c_kn, 0),
          raport: nr(reg?.l0_h, 2),
          care: t((reg?.l0_h ?? 0) >= 1
            ? "struct.arp.verticale_mai_eficiente"
            : "struct.arp.orizontale_devin_eficiente"),
        })}
        formule={
          p.nod_reazem
            ? [
                {
                  eticheta: t("struct.arp.unghiul_forta_biela"),
                  valori: `tgθ = z/(l₀/2) ⇒ θ = ${nr(p.nod_reazem.theta_grade, 1)}°; F_biela = V_Ed/sinθ = ${nr(p.nod_reazem.F_biela_kn, 0)} kN`,
                  ref: "SR EN 1992-1-1 §6.5.4",
                },
                {
                  eticheta: t("struct.arp.presiunea_nod_vs"),
                  valori: `σ = ${nr(p.nod_reazem.F_biela_kn, 0)}/${nr(p.nod_reazem.aria_m2, 4)} = ${nr(p.nod_reazem.sigma_kpa / 1000, 2)} MPa ≤ ${nr(p.nod_reazem.sigma_rd_kpa / 1000, 2)} MPa`,
                  ref: t("struct.arp.ref_nod_cct"),
                },
              ]
            : undefined
        }
      >
        <div style={{ overflowX: "auto" }}>
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("struct.arp.th_verificarea")}</th>
                <th>{t("struct.arp.th_punct")}</th>
                <th>{t("struct.arp.th_grupare")}</th>
                <th>E</th>
                <th>R</th>
                <th>R/E</th>
                <th>{t("struct.arp.th_verdict")}</th>
              </tr>
            </thead>
            <tbody>
              {[
                ...verif("V în plan"),
                ...verif("Biela comprimată"),
                ...verif("Nodul de reazem"),
              ].map((v, i) => (
                <tr key={i}>
                  <td>
                    <TitluVerificare catalog={catalog} pas="armare_pereti"
                                     nume={v.nume} grupa={v.grupa}
                                     text={v.nume} aratNumele={false}
                                     aratPasul={false} />
                    {v.referinta && (
                      <div className="sg-muted" style={{ fontSize: 11 }}>{v.referinta}</div>
                    )}
                  </td>
                  <td className="sg-mono">{punctScurt(v.punct) ?? "—"}</td>
                  <td className="sg-muted">{v.grupa}</td>
                  <td className="sg-mono">{typeof v.E === "number" ? nr(v.E) : v.E}</td>
                  <td className="sg-mono"><strong>{typeof v.R === "number" ? nr(v.R) : v.R}</strong></td>
                  <td className="sg-mono">{v.RE == null ? "—" : nr(v.RE, 2)}</td>
                  <td><Verdict v={v.verdict} marja={v.acceptat_marja} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Sectiune>

      {/* --- 7. Rostul de lucru ------------------------------------------- */}
      {p.rost && (
        <Sectiune
          titlu={t("struct.arp.7_lunecarea_rosturile")}
          explicatie={t("struct.arp.s7_explicatie", {
            c: nr(par?.rost?.c, 2), mu: nr(par?.rost?.mu, 1),
            cguv: nr(p.rost.c, 2),
          })}
          formule={[
            {
              eticheta: t("struct.arp.efortul_lunecare_vedi"),
              valori: `v_Edi = 1,0·${nr(Math.abs((p.puncte.find((q) => q.punct === p.rost!.punct && q.grupa === p.rost!.grupa)?.V2_kn) ?? 0), 0)} / (${nr(p.rost.z_m, 2)}·${nr(p.t_cm / 100, 2)}) = ${nr(p.rost.v_edi_kpa / 1000, 3)} MPa`,
              ref: t("struct.arp.ref_rost_vedi", { punct: traduceRezultat(punctScurt(p.rost.punct ?? "")), grupa: traduceRezultat(p.rost.grupa) }),
            },
            {
              eticheta: t("struct.arp.rezistenta_v_rdi"),
              valori: `v_Rdi = ${nr(p.rost.termen_beton_kpa / 1000, 3)} + ${nr((p.rost.mu * p.rost.sigma_n_kpa) / 1000, 3)} + ${nr(p.rost.termen_otel_kpa / 1000, 3)} = ${nr(p.rost.v_rdi_brut_kpa / 1000, 3)} MPa ≤ 0,5·ν·f_cd = ${nr(p.rost.plafon_kpa / 1000, 2)} MPa`,
              ref: t("struct.arp.ref_rost_vrdi", { rho: nr(p.rost.rho * 100, 3), phi: a.vertical.phi_mm, pas: a.vertical.pas_mm, sigma: nr(p.rost.sigma_n_kpa / 1000, 3) }),
            },
          ]}
        >
          <div style={{ overflowX: "auto" }}>
            <table className="sg-table">
              <thead>
                <tr>
                  <th>{t("struct.arp.th_punct")}</th>
                  <th>{t("struct.arp.th_grupare")}</th>
                  <th>E = v_Edi (kPa)</th>
                  <th>R = v_Rdi (kPa)</th>
                  <th>R/E</th>
                  <th>{t("struct.arp.th_verdict")}</th>
                </tr>
              </thead>
              <tbody>
                {verif("Lunecarea în rostul de lucru").map((v, i) => (
                  <tr key={i}>
                    <td className="sg-mono">{punctScurt(v.punct)}</td>
                    <td className="sg-muted">{v.grupa}</td>
                    <td className="sg-mono">{typeof v.E === "number" ? nr(v.E) : v.E}</td>
                    <td className="sg-mono"><strong>{typeof v.R === "number" ? nr(v.R) : v.R}</strong></td>
                    <td className="sg-mono">{v.RE == null ? "—" : nr(v.RE, 2)}</td>
                    <td><Verdict v={v.verdict} marja={v.acceptat_marja} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="sg-muted" style={{ marginTop: 6, fontSize: 12 }}>
            {t("struct.arp.din_capabili", {
              mpa: nr(p.rost.v_rdi_brut_kpa / 1000, 2),
              pct: nr((100 * p.rost.termen_otel_kpa)
                / Math.max(p.rost.v_rdi_brut_kpa, 1e-9), 0),
            })}
          </div>
        </Sectiune>
      )}

      {/* --- 8. SLS: fisurile -------------------------------------------- */}
      {p.sls && (
        <Sectiune
          titlu={t("struct.arp.8_deschiderea_fisurilor")}
          explicatie={t("struct.arp.s8_explicatie", {
            wmax: nr(p.sls.w_max_mm, 1), eeff: nr(p.sls.E_eff_mpa, 0),
            fi: nr(p.sls.phi_fluaj, 1), mcr: nr(p.sls.M_cr_knm, 0),
          })}
        >
          <div style={{ overflowX: "auto" }}>
            <table className="sg-table">
              <thead>
                <tr>
                  <th>{t("struct.arp.th_punct")}</th>
                  <th title={t("struct.arp.momentul_cvasipermanent")}>M_GS (kN·m)</th>
                  <th title={t("struct.arp.armatura_intinsa_conside")}>A_s (cm²)</th>
                  <th title={t("struct.arp.efortul_armatura_intinsa")}>σ_s (MPa)</th>
                  <th title={t("struct.arp.distanta_maxima_intre")}>s_r,max (mm)</th>
                  <th>w_k (mm)</th>
                  <th>{t("struct.arp.th_limita")}</th>
                  <th>{t("struct.arp.th_verdict")}</th>
                </tr>
              </thead>
              <tbody>
                {p.sls.fisuri.map((f, i) => (
                  <tr key={i}>
                    <td className="sg-mono" title={numelePunctului(f.punct)}>
                      {punctScurt(f.punct)}
                    </td>
                    <td className="sg-mono">{nr(f.M_knm)}</td>
                    <td className="sg-mono">{nr(f.As_cm2, 2)}</td>
                    <td className="sg-mono">{f.sigma_s != null ? nr(f.sigma_s) : "—"}</td>
                    <td className="sg-mono">{f.s_rmax_mm != null ? nr(f.s_rmax_mm, 0) : "—"}</td>
                    <td className="sg-mono"><strong>{nr(f.wk_mm, 3)}</strong></td>
                    <td className="sg-mono">{nr(p.sls!.w_max_mm, 1)}</td>
                    <td><span className={verdictClass(f.verdict)}>{textVerdict(f.verdict)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Sectiune>
      )}

      {/* --- 9. Reguli constructive -------------------------------------- */}
      <Sectiune
        titlu={t("struct.arp.9_regulile_constructive")}
        explicatie={t("struct.arp.s9_explicatie", {
          fel: t(p.perimetral
            ? "struct.arp.perete_de_contur" : "struct.arp.perete_interior"),
          minim: p.perimetral ? "25" : "20",
        })}
      >
        <div style={{ overflowX: "auto" }}>
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("struct.arp.th_verificarea")}</th>
                <th>{t("struct.arp.th_e_adoptat")}</th>
                <th>{t("struct.arp.th_r_limita")}</th>
                <th>R/E</th>
                <th>{t("struct.arp.th_verdict")}</th>
              </tr>
            </thead>
            <tbody>
              {p.verificari
                .filter((v) => v.grupa === "constructiv")
                .map((v, i) => (
                  <tr key={i}>
                    <td>
                      <TitluVerificare catalog={catalog} pas="armare_pereti"
                                       nume={v.nume} grupa={v.grupa}
                                       text={v.nume} aratNumele={false}
                                       aratPasul={false} />
                      {v.referinta && (
                        <div className="sg-muted" style={{ fontSize: 11 }}>{v.referinta}</div>
                      )}
                      {(v.nota || v.motiv_neaplicare) && (
                        <div className="sg-muted" style={{ fontSize: 11 }}>
                          {v.nota || v.motiv_neaplicare}
                        </div>
                      )}
                    </td>
                    <td className="sg-mono">
                      {v.E == null ? "—" : typeof v.E === "number" ? nr(v.E, 3) : v.E}
                    </td>
                    <td className="sg-mono">{typeof v.R === "number" ? nr(v.R, 3) : v.R}</td>
                    <td className="sg-mono">{v.RE == null ? "—" : nr(v.RE, 2)}</td>
                    <td>
                      {v.nu_se_aplica
                        ? <span className="sg-muted">{t("struct.arp.aplica2")}</span>
                        : <Verdict v={v.verdict} marja={v.acceptat_marja} />}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Sectiune>

      {/* --- 10. Ancorarea mustăților ------------------------------------ */}
      {p.ancorare && (
        <Sectiune
          titlu={t("struct.arp.10_ancorarea_innadirea")}
          explicatie={t("struct.arp.s10_explicatie", {
            alfa6: nr(p.ancorare.alfa_6, 1),
          })}
          formule={[
            {
              eticheta: t("struct.arp.aderenta_proiectare_f"),
              valori: t("struct.arp.val_fbd", {
                fctd: nr(par?.f_ctd_mpa, 2), fbd: nr(p.ancorare.f_bd_mpa, 2),
              }),
              ref: "SR EN 1992-1-1 §8.4.2",
            },
            {
              eticheta: t("struct.arp.lungimea_ancorare_baza"),
              valori: t("struct.arp.val_lb", {
                phi: a.vertical.phi_mm, fyd: par?.f_yd_mpa,
                fbd: nr(p.ancorare.f_bd_mpa, 2),
                lb: nr(p.ancorare.l_b_rqd_cm, 1), lbd: nr(p.ancorare.l_bd_cm, 1),
              }),
              ref: t("struct.arp.ref_minime_ancorare"),
            },
            {
              eticheta: t("struct.arp.lungimea_innadire_a"),
              valori: t("struct.arp.val_l0", {
                a6: nr(p.ancorare.alfa_6, 1), lb: nr(p.ancorare.l_b_rqd_cm, 1),
                l0: nr(p.ancorare.l_0_cm, 1),
              }),
              ref: t("struct.arp.ref_innadite_aceeasi"),
            },
          ]}
        >
          <div className="sg-muted" style={{ fontSize: 13 }}>
            {tx("struct.arp.practic_plansa", {
              phi: a.vertical.phi_mm, pas: a.vertical.pas_mm,
              l0: nr(p.ancorare.l_0_cm, 0), h: nr(p.h_cm, 0),
              rezerva: nr(p.h_cm - p.ancorare.l_0_cm, 0),
            })}
          </div>
        </Sectiune>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Desenele 2D
// ---------------------------------------------------------------------------
// Un tabel spune „Ø10/250 pe ambele fețe"; desenele arată CE înseamnă asta la
// cofraj. Aceeași convenție de citire (și aceleași culori) ca la secțiunile
// grinzilor din Pasul 13.

export function SectiunePereteGrosime({
  p,
  acoperire,
}: {
  p: ArmarePerete;
  acoperire: number;
}) {
  const gros = p.t_cm;
  const c = acoperire;
  const v = p.armare.vertical;
  const o = p.armare.orizontal;
  // se desenează o fâșie de perete de 3 pași de bare verticale, văzută DE SUS
  const pas = v.pas_mm / 10;
  const latime = pas * 3;
  const sc = 3.0;
  // caseta face loc cotelor: rai la stânga (gros), unul deasupra (pasul barelor)
  // și unul dedesubt (fâșia) — Stefan
  const ox = 128,
    // titlul secțiunii e lung: cota pasului are nevoie de aer sub el
    oy = 54;
  const W = ox + latime * sc + 210,
    H = oy + gros * sc + 92;
  const rV = Math.max((v.phi_mm / 20) * sc, 1.6);
  const rO = Math.max((o.phi_mm / 20) * sc, 1.4);
  const yFata = [c, gros - c];
  const xBare = [0.5, 1.5, 2.5].map((k) => k * pas);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="ap-svg" style={{ width: W, maxWidth: "100%" }}>
      <text x={4} y={14} fontSize={12} fill="#555">
        {t("struct.arp.svg_a_sectiune_grosime", { t: nr(gros, 0) })}
      </text>
      <rect x={ox} y={oy} width={latime * sc} height={gros * sc} fill={COL.beton} stroke={COL.betonLinie} />
      {/* barele ORIZONTALE de repartiție, tăiate de secțiune */}
      {yFata.map((y, i) =>
        xBare.map((x, j) => (
          <circle key={`o${i}${j}`} cx={ox + x * sc} cy={oy + y * sc} r={rO} fill={COL.orizontal} />
        ))
      )}
      {/* barele VERTICALE, lipite de plasa fiecărei fețe */}
      {yFata.map((y, i) =>
        xBare.map((x, j) => (
          <circle
            key={`v${i}${j}`}
            cx={ox + (x + pas * 0.25) * sc}
            cy={oy + y * sc}
            r={rV}
            fill={COL.vertical}
          />
        ))
      )}
      {/* agrafele care leagă cele două plase */}
      {xBare.map((x, j) => (
        <path
          key={`a${j}`}
          d={`M ${ox + (x - pas * 0.2) * sc} ${oy + yFata[0] * sc} L ${ox + (x - pas * 0.2) * sc} ${oy + yFata[1] * sc}`}
          stroke={COL.agrafe}
          strokeWidth={1.4}
          fill="none"
        />
      ))}
      {/* COTELE: grosimea, pasul barelor, acoperirea, fâșia desenată */}
      <CotaV x={ox - 30} y1={oy} y2={oy + gros * sc} martorX={ox}
             text={`t = ${cmFmt(gros)} cm`} />
      <CotaV x={ox - 66} y1={oy} y2={oy + c * sc} martorX={ox}
             text={`c = ${nr(c, 1)} cm`} size={9} />
      <CotaH x1={ox + xBare[0] * sc} x2={ox + xBare[1] * sc} y={oy - 16}
             martorY={oy} deasupra text={t("struct.arp.cota_pas", { v: v.pas_mm })} />
      <CotaH x1={ox} x2={ox + latime * sc} y={oy + gros * sc + 26}
             martorY={oy + gros * sc}
             text={t("struct.arp.cota_fasie", { v: nr(latime, 0) })} />
      <text x={ox} y={oy + gros * sc + 56} fontSize={11} fill="#777">
        {t("struct.arp.svg_acoperire_panou", {
          c: nr(c), l0: nr(p.l0_m, 2), h: nr(p.h_cm / 100, 2),
        })}
      </text>
      <text x={ox + latime * sc + 28} y={oy + 12} fontSize={11} fill={COL.vertical}>
        {t("struct.arp.svg_verticale", { phi: v.phi_mm, pas: v.pas_mm })}
      </text>
      <text x={ox + latime * sc + 28} y={oy + 28} fontSize={11} fill={COL.orizontal}>
        {t("struct.arp.svg_orizontale", { phi: o.phi_mm, pas: o.pas_mm })}
      </text>
      <text x={ox + latime * sc + 28} y={oy + 44} fontSize={11} fill={COL.agrafe}>
        {t("struct.arp.svg_agrafe", {
          n: p.armare.agrafe.per_m2, phi: p.armare.agrafe.phi_mm,
        })}
      </text>
    </svg>
  );
}

// Secțiunea VERTICALĂ prin panou (grosime × înălțime): singurul desen în care
// se vede banda tirantului și cele două rosturi de lucru.
function SectiuneVerticalaPerete({
  p,
  acoperire,
}: {
  p: ArmarePerete;
  acoperire: number;
}) {
  const gros = p.t_cm,
    h = p.h_cm;
  const c = acoperire;
  const o = p.armare.orizontal;
  const v = p.armare.vertical;
  const banda = p.regim?.banda_tirant_cm ?? 0;
  const gp = p.regim?.grinda_perete;
  const sc = Math.min(1.15, 300 / h);
  const ox = 54,
    oy = 30;
  // legendele descriptive NU stau în SVG: textul SVG nu rupe rândurile, deci
  // o frază mai lată decât viewBox-ul se taie la margine fără să se vadă că
  // lipsește. Rămân doar etichetele scurte din desen; restul e HTML dedesubt.
  // W: panoul e îngust, dar în dreapta lui stau etichetele celor două rosturi
  // și titlul, deci lățimea minimă e dictată de text, nu de secțiune
  const W = Math.max(gros * sc + 250, 320),
    H = h * sc + 48;
  const rO = Math.max((o.phi_mm / 20) * sc * 2.4, 1.5);
  const pasO = o.pas_mm / 10;
  const nO = Math.max(Math.floor((h - 2 * c) / pasO) + 1, 2);
  const yFata = [c, gros - c];
  const dTir = p.armare.d1_cm ?? c + 0.5;
  const ts = p.armare.tirant_sus,
    tj = p.armare.tirant_jos;
  const scT = gros * sc < 26 ? 26 / (gros * sc) : 1; // panourile subțiri: lățime minimă lizibilă
  const wRect = gros * sc * scT;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="ap-svg" style={{ width: W, maxWidth: "100%" }}>
      <text x={4} y={14} fontSize={12} fill="#555">
        {t("struct.arp.svg_b_sectiune_verticala", { t: nr(gros, 0), h: nr(h, 0) })}
      </text>
      {/* placa peste subsol + grinda de fundare, ca reper al rosturilor */}
      <rect x={ox - 10} y={oy - 12} width={wRect + 20} height={10} fill={COL.placa} stroke={COL.placaLinie} />
      <rect x={ox - 10} y={oy + h * sc + 2} width={wRect + 20} height={10} fill={COL.placa} stroke={COL.placaLinie} />
      <rect x={ox} y={oy} width={wRect} height={h * sc} fill={COL.beton} stroke={COL.betonLinie} />
      {/* benzile întinse, sus și jos */}
      {gp && (
        <>
          <rect x={ox} y={oy} width={wRect} height={banda * sc} fill={COL.tirant} opacity={0.13} />
          <rect
            x={ox}
            y={oy + (h - banda) * sc}
            width={wRect}
            height={banda * sc}
            fill={COL.tirant}
            opacity={0.13}
          />
        </>
      )}
      {/* barele VERTICALE, câte una pe fiecare față, prelungite sub panou ca
          mustăți în grinda de fundare — ele sunt conectorii ambelor rosturi */}
      {yFata.map((xf, k) => (
        <line
          key={`v${k}`}
          x1={ox + xf * sc * scT}
          y1={oy - 6}
          x2={ox + xf * sc * scT}
          y2={oy + h * sc + 12}
          stroke={COL.vertical}
          strokeWidth={Math.max((v.phi_mm / 20) * sc * 2.2, 1.8)}
        />
      ))}
      {/* rândurile plasei orizontale, pe ambele fețe */}
      {Array.from({ length: nO }, (_, i) => {
        const y = c + i * pasO;
        if (y > h - c) return null;
        return yFata.map((xf, k) => (
          <circle
            key={`o${i}${k}`}
            cx={ox + xf * sc * scT}
            cy={oy + y * sc}
            r={rO}
            fill={COL.orizontal}
          />
        ));
      })}
      {/* tiranții concentrați, lipiți de fața întinsă */}
      {ts.n > 0 &&
        yFata.map((xf, k) => (
          <circle
            key={`ts${k}`}
            cx={ox + xf * sc * scT}
            cy={oy + dTir * sc}
            r={rO * 1.8}
            fill={COL.tirant}
            stroke="#a97614"
          />
        ))}
      {tj.n > 0 &&
        yFata.map((xf, k) => (
          <circle
            key={`tj${k}`}
            cx={ox + xf * sc * scT}
            cy={oy + (h - dTir) * sc}
            r={rO * 1.8}
            fill={COL.tirant}
            stroke="#a97614"
          />
        ))}
      {/* rosturile de lucru */}
      {[oy, oy + h * sc].map((y, i) => (
        <g key={`r${i}`}>
          <line
            x1={ox - 14}
            y1={y}
            x2={ox + wRect + 14}
            y2={y}
            stroke={COL.rost}
            strokeWidth={2}
            strokeDasharray="5 3"
          />
          <text x={ox + wRect + 18} y={y + 4} fontSize={10.5} fill={COL.rost}>
            {t(i === 0 ? "struct.arp.rost_perete_placa"
                       : "struct.arp.rost_talpa_perete")}
          </text>
        </g>
      ))}
    </svg>
  );
}

function ElevatiePerete({ p }: { p: ArmarePerete }) {
  const L = p.L_ax_m * 100;
  const h = p.h_cm;
  const v = p.armare.vertical;
  const o = p.armare.orizontal;
  const banda = p.regim?.banda_tirant_cm ?? 0;
  const gp = p.regim?.grinda_perete;
  const pasV = v.pas_mm / 10;
  const pasO = o.pas_mm / 10;
  const sc = Math.min(1.05, 640 / L);
  const ox = 52,
    oy = 34;
  const nV = Math.max(Math.floor(L / pasV), 1);
  const nO = Math.max(Math.floor(h / pasO), 1);
  const offI = (p.off_i_m ?? 0) * 100;
  const offJ = (p.off_j_m ?? 0) * 100;
  // cele 5 puncte, cu fața întinsă din gruparea guvernantă (seismica dacă e)
  const seism = p.puncte.filter((q) => q.grupa === "seismica");
  const sursa = seism.length ? seism : p.puncte;
  const puncte = PUNCTE.map((pct) => sursa.find((q) => q.punct === pct)).filter(
    (q): q is NonNullable<typeof q> => !!q
  );
  // Etichetele celor 5 puncte se așază pe RÂNDURI, nu toate pe același: la un
  // panou scurt extremul de câmp cade lângă reazem (diagrama seismică e
  // aproape liniară), iar „câmp" se suprapunea cu „R2 sus" până nu se mai
  // putea citi. Fiecare etichetă intră pe primul rând liber la ≥ 58 px —
  // atât ocupă rândul al doilea, cel cu «−501 · sus».
  const LAT_ETICHETA = 58;
  const randuri: number[] = [];
  const etichete = puncte
    .map((q) => ({ q, x: ox + Math.min(Math.max(q.loc_m * 100, 0), L) * sc }))
    .sort((a, b) => a.x - b.x)
    .map(({ q, x }) => {
      let rand = 0;
      while (rand < randuri.length && x - randuri[rand] < LAT_ETICHETA) rand++;
      randuri[rand] = x;
      return { q, x, rand };
    });
  const nRanduri = Math.max(randuri.length, 1);
  // H cuprinde cotele punctelor (oy + h·sc + 30 + rând·24 + 11); legendele
  // descriptive sunt HTML dedesubt, fiindcă textul SVG nu rupe rândurile
  const W = L * sc + 90,
    H = h * sc + 34 + 30 + nRanduri * 24;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="ap-svg" style={{ width: W, maxWidth: "100%" }}>
      <text x={4} y={14} fontSize={12} fill="#555">
        {t("struct.arp.svg_c_elevatia", {
          l: nr(p.L_ax_m, 2), h: nr(h / 100, 2),
          si: p.stalp_i ?? "?", sj: p.stalp_j ?? "?",
        })}
      </text>
      {/* placa peste subsol */}
      <rect x={ox - 20} y={oy - 14} width={L * sc + 40} height={9} fill={COL.placa} stroke={COL.placaLinie} />
      {/* grinda de fundare */}
      <rect x={ox - 20} y={oy + h * sc + 5} width={L * sc + 40} height={11} fill={COL.placa} stroke={COL.placaLinie} />
      {/* stâlpii de la capete */}
      <rect x={ox - 18} y={oy - 5} width={18} height={h * sc + 10} fill={COL.stalp} stroke={COL.stalpLinie} />
      <rect x={ox + L * sc} y={oy - 5} width={18} height={h * sc + 10} fill={COL.stalp} stroke={COL.stalpLinie} />
      <rect x={ox} y={oy} width={L * sc} height={h * sc} fill={COL.beton} stroke={COL.betonLinie} />
      {/* zonele rigide (bucata de panou din interiorul stâlpului) */}
      {[
        [0, offI],
        [L - offJ, offJ],
      ].map(([xs, len], i) =>
        len > 0.1 ? (
          <rect
            key={`rig${i}`}
            x={ox + xs * sc}
            y={oy}
            width={len * sc}
            height={h * sc}
            fill={COL.rost}
            opacity={0.16}
          />
        ) : null
      )}
      {/* benzile întinse */}
      {gp && (
        <>
          <rect x={ox} y={oy} width={L * sc} height={banda * sc} fill={COL.tirant} opacity={0.1} />
          <rect x={ox} y={oy + (h - banda) * sc} width={L * sc} height={banda * sc} fill={COL.tirant} opacity={0.1} />
        </>
      )}
      {/* plasele */}
      {Array.from({ length: nV + 1 }, (_, i) => (
        <line
          key={`v${i}`}
          x1={ox + i * pasV * sc}
          y1={oy}
          x2={ox + i * pasV * sc}
          y2={oy + h * sc}
          stroke={COL.vertical}
          strokeWidth={0.9}
          opacity={0.85}
        />
      ))}
      {Array.from({ length: nO + 1 }, (_, i) => (
        <line
          key={`o${i}`}
          x1={ox}
          y1={oy + i * pasO * sc}
          x2={ox + L * sc}
          y2={oy + i * pasO * sc}
          stroke={COL.orizontal}
          strokeWidth={0.9}
          opacity={0.85}
        />
      ))}
      {/* tiranții, ca linii groase în bandă */}
      {gp && p.armare.tirant_sus.n > 0 && (
        <line x1={ox} y1={oy + 5} x2={ox + L * sc} y2={oy + 5} stroke={COL.tirant} strokeWidth={3.2} />
      )}
      {gp && p.armare.tirant_jos.n > 0 && (
        <line
          x1={ox}
          y1={oy + h * sc - 5}
          x2={ox + L * sc}
          y2={oy + h * sc - 5}
          stroke={COL.tirant}
          strokeWidth={3.2}
        />
      )}
      {/* rosturile de lucru */}
      {[oy, oy + h * sc].map((y, i) => (
        <line
          key={`rst${i}`}
          x1={ox - 20}
          y1={y}
          x2={ox + L * sc + 20}
          y2={y}
          stroke={COL.rost}
          strokeWidth={2}
          strokeDasharray="6 4"
        />
      ))}
      {/* cele 5 puncte de verificare, cu fața întinsă și eticheta pe rândul
          care nu se suprapune cu vecinul */}
      {etichete.map(({ q, x, rand }, i) => {
        const sus = q.M3_knm < 0;
        const yEt = oy + h * sc + 30 + rand * 24;
        return (
          <g key={`pt${i}`}>
            {/* linia de indicație se oprește deasupra PRIMULUI rând de
                etichete, nu al rândului propriu: R1 sus și R1 jos au același
                x, deci o linie coborâtă până la rândul 2 ar trece exact peste
                textul rândului 1 */}
            <line
              x1={x}
              y1={oy - 2}
              x2={x}
              y2={oy + h * sc + 18}
              stroke="#232323"
              strokeWidth={1}
              strokeDasharray="3 3"
            />
            <circle cx={x} cy={sus ? oy + 8 : oy + h * sc - 8} r={4.6} fill="#232323" />
            <text x={x} y={yEt} fontSize={10} fill="#232323" textAnchor="middle">
              {punctScurt(q.punct)}
            </text>
            <text x={x} y={yEt + 11} fontSize={9.5} fill="#777" textAnchor="middle">
              {nr(q.M3_knm, 0)} · {t(sus ? "struct.arp.fata_sus" : "struct.arp.fata_jos")}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// 3D — panoul cu armătura și contextul
// ---------------------------------------------------------------------------

export function Perete3D({ p }: { p: ArmarePerete }) {
  useT();
  const mount = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = mount.current;
    if (!el) return;
    const W = el.clientWidth || 900;
    const H = 380;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf3f6f9);
    const camera = new THREE.PerspectiveCamera(40, W / H, 0.01, 200);
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      preserveDrawingBuffer: true,
    });
    renderer.setSize(W, H);
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    el.appendChild(renderer.domElement);

    // tot ce se alocă pe GPU și nu e prins de renderer.dispose() se strânge
    // aici; altfel schimbarea panoului din dropdown scurge sprite-uri
    const gunoi: { dispose(): void }[] = [];

    const L = p.L_ax_m;
    const grosM = p.t_cm / 100;
    const h = p.h_cm / 100;
    const c = 0.035;
    const offI = p.off_i_m ?? 0;
    const offJ = p.off_j_m ?? 0;
    const banda = (p.regim?.banda_tirant_cm ?? 0) / 100;
    const gp = !!p.regim?.grinda_perete;

    // sistemul: X = lungul panoului, Y = grosimea, Z = pe verticală, cu fața
    // de SUS a panoului la z = 0 (ca la grinzi) și panoul coborând la z = −h
    const mat = (col: number, op: number) =>
      new THREE.MeshStandardMaterial({
        color: col,
        transparent: true,
        opacity: op,
        depthWrite: false,
      });

    const geoP = new THREE.BoxGeometry(L, grosM, h);
    gunoi.push(geoP);
    const beton = new THREE.Mesh(geoP, mat(0x9aa0a8, 0.2));
    beton.position.set(L / 2, 0, -h / 2);
    scene.add(beton);
    const geoPM = new THREE.EdgesGeometry(geoP);
    gunoi.push(geoPM);
    const matPM = new THREE.LineBasicMaterial({ color: 0x777777 });
    gunoi.push(matPM);
    const muchiiP = new THREE.LineSegments(geoPM, matPM);
    muchiiP.position.copy(beton.position);
    scene.add(muchiiP);

    // placa peste subsol (deasupra) + grinda de fundare (dedesubt)
    for (const [zc, gros, col] of [
      [0.075, 0.15, 0xb9bfc7],
      [-h - 0.2, 0.4, 0xa9b0b8],
    ] as [number, number, number][]) {
      const geo = new THREE.BoxGeometry(L + 2 * Math.max(offI, offJ) + 0.6, Math.max(grosM * 2.2, 0.5), gros);
      gunoi.push(geo);
      const m = new THREE.Mesh(geo, mat(col, 0.16));
      m.position.set(L / 2, 0, zc);
      scene.add(m);
    }

    // stâlpii de capăt
    for (const [x0, off] of [
      [0, offI],
      [L, offJ],
    ] as [number, number][]) {
      if (!(off > 0.001)) continue;
      const geo = new THREE.BoxGeometry(2 * off, Math.max(grosM * 1.6, 0.3), h + 0.5);
      gunoi.push(geo);
      const st = new THREE.Mesh(geo, mat(0x7d858f, 0.3));
      st.position.set(x0, 0, -h / 2);
      scene.add(st);
      const geoM = new THREE.EdgesGeometry(geo);
      gunoi.push(geoM);
      const mm = new THREE.LineBasicMaterial({ color: 0x5e2449 });
      gunoi.push(mm);
      const mu = new THREE.LineSegments(geoM, mm);
      mu.position.copy(st.position);
      scene.add(mu);
    }

    const yFata = [-(grosM / 2 - c), grosM / 2 - c];

    // barele ORIZONTALE de repartiție: cilindri în lungul panoului, pe ambele
    // fețe, la fiecare pas pe verticală
    const matO = new THREE.MeshStandardMaterial({ color: 0x1f7a8c });
    gunoi.push(matO);
    const pasO = p.armare.orizontal.pas_mm / 1000;
    const phiO = p.armare.orizontal.phi_mm / 2000;
    const geoO = new THREE.CylinderGeometry(phiO, phiO, L + offI + offJ, 8);
    gunoi.push(geoO);
    for (let z = -c; z > -h + c; z -= pasO) {
      for (const y of yFata) {
        const m = new THREE.Mesh(geoO, matO);
        m.rotation.z = Math.PI / 2;
        m.position.set(L / 2, y, z);
        scene.add(m);
      }
    }

    // TIRANȚII din banda întinsă — bare concentrate, mai groase, în ocru
    if (gp) {
      const matT = new THREE.MeshStandardMaterial({ color: 0xa97614 });
      gunoi.push(matT);
      for (const [set, zBaza] of [
        [p.armare.tirant_sus, -c],
        [p.armare.tirant_jos, -h + c],
      ] as [{ n: number; phi_mm: number }, number][]) {
        if (!set.n) continue;
        const phiT = set.phi_mm / 2000;
        const geoT = new THREE.CylinderGeometry(phiT, phiT, L + offI + offJ, 10);
        gunoi.push(geoT);
        // n bare pe cele două fețe ⇒ n/2 rânduri pe față, în banda
        const nRand = Math.max(Math.ceil(set.n / 2), 1);
        for (let i = 0; i < nRand; i++) {
          const dz = (i * Math.min(banda * 0.6, 0.25)) / Math.max(nRand - 1, 1);
          for (const y of yFata) {
            const m = new THREE.Mesh(geoT, matT);
            m.rotation.z = Math.PI / 2;
            m.position.set(L / 2, y, zBaza < -h / 2 ? zBaza + dz : zBaza - dz);
            scene.add(m);
          }
        }
      }
    }

    // barele VERTICALE: cilindri pe înălțime, prelungiți SUB panou ca mustăți
    const matV = new THREE.MeshStandardMaterial({ color: 0xc0392b });
    gunoi.push(matV);
    const pasV = p.armare.vertical.pas_mm / 1000;
    const phiV = p.armare.vertical.phi_mm / 2000;
    const lungV = h + 0.35; // mustața intră în grinda de fundare
    const geoV = new THREE.CylinderGeometry(phiV, phiV, lungV, 8);
    gunoi.push(geoV);
    for (let x = pasV / 2; x < L; x += pasV) {
      for (const y of yFata) {
        const m = new THREE.Mesh(geoV, matV);
        // axa unui CylinderGeometry e Y, iar Y e GROSIMEA peretelui în scena
        // asta: fără rotație barele ieșeau perpendicular pe perete, ~1,35 m
        // în fiecare parte, la jumătatea înălțimii.
        // Rotația în jurul lui X duce axa din Y în Z, adică pe verticală.
        m.rotation.x = Math.PI / 2;
        m.position.set(x, y, -h / 2 - 0.14);
        scene.add(m);
      }
    }

    // AGRAFELE care leagă plasele: segmente pe grosime, ~1/m²
    const ptsA: number[] = [];
    const stepA = Math.max(1 / Math.sqrt(Math.max(p.armare.agrafe.per_m2, 1)), 0.35);
    for (let x = stepA / 2; x < L; x += stepA) {
      for (let z = -stepA / 2; z > -h; z -= stepA) {
        ptsA.push(x, yFata[0], z, x, yFata[1], z);
      }
    }
    if (ptsA.length) {
      const geoA = new THREE.BufferGeometry();
      geoA.setAttribute("position", new THREE.Float32BufferAttribute(ptsA, 3));
      gunoi.push(geoA);
      const matA = new THREE.LineBasicMaterial({ color: 0x8e44ad });
      gunoi.push(matA);
      scene.add(new THREE.LineSegments(geoA, matA));
    }

    // etichete = sprite-uri din canvas (aceeași rețetă ca la grinzi)
    const swT = Math.min(1.7, Math.max(0.55, L * 0.13));
    const eticheta = (txt: string, x: number, y: number, z: number,
                      col = "#b8730c", w = swT) => {
      const cv = document.createElement("canvas");
      cv.width = 512;
      cv.height = 64;
      const g2 = cv.getContext("2d");
      if (!g2) return;
      g2.fillStyle = col;
      g2.font = "bold 32px system-ui, sans-serif";
      g2.textAlign = "center";
      g2.textBaseline = "middle";
      g2.fillText(txt, 256, 34);
      const tex = new THREE.CanvasTexture(cv);
      const m = new THREE.SpriteMaterial({ map: tex, transparent: true });
      const sp = new THREE.Sprite(m);
      sp.position.set(x, y, z);
      sp.scale.set(w, (w * cv.height) / cv.width, 1);
      scene.add(sp);
      gunoi.push(tex, m);
    };

    // ROSTURILE DE LUCRU: două plane prună, la baza și la vârful panoului
    const matRost = new THREE.MeshStandardMaterial({
      color: 0x8e3b73,
      transparent: true,
      opacity: 0.4,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    gunoi.push(matRost);
    for (const [z, nume] of [
      [0, t("struct.arp.rost_perete_placa")],
      [-h, t("struct.arp.rost_talpa_perete")],
    ] as [number, string][]) {
      const geo = new THREE.BoxGeometry(L, grosM * 1.08, 0.012);
      gunoi.push(geo);
      const m = new THREE.Mesh(geo, matRost);
      m.position.set(L / 2, 0, z);
      scene.add(m);
      eticheta(nume, L / 2, grosM * 1.6, z, "#b8730c", swT * 1.15);
    }

    // banda tirantului, marcată ca volum translucid
    if (gp && banda > 0.01) {
      const matB = new THREE.MeshStandardMaterial({
        color: 0xa97614,
        transparent: true,
        opacity: 0.16,
        depthWrite: false,
      });
      gunoi.push(matB);
      for (const zc of [-banda / 2, -h + banda / 2]) {
        const geo = new THREE.BoxGeometry(L, grosM * 1.01, banda);
        gunoi.push(geo);
        const m = new THREE.Mesh(geo, matB);
        m.position.set(L / 2, 0, zc);
        scene.add(m);
      }
      eticheta(t("struct.arp.eticheta_banda_tirant", { v: nr(banda * 100, 0) }),
               L / 2, 0, -banda / 2, "#a97614", swT);
    }

    // cota l₀ între fețele stâlpilor
    const xa = offI,
      xb = L - offJ;
    if (xb > xa) {
      const zEt = -h - 0.55;
      const zc = zEt - 0.1,
        tick = 0.07;
      const geoC = new THREE.BufferGeometry();
      geoC.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(
          [xa, 0, zc, xb, 0, zc, xa, 0, zc - tick, xa, 0, zc + tick, xb, 0, zc - tick, xb, 0, zc + tick],
          3
        )
      );
      gunoi.push(geoC);
      const matC = new THREE.LineBasicMaterial({ color: 0x2c3e70 });
      gunoi.push(matC);
      scene.add(new THREE.LineSegments(geoC, matC));
      eticheta(`l₀ = ${nr(p.l0_m, 2)} m  (l₀/h = ${nr(p.regim?.l0_h, 2)})`,
               (xa + xb) / 2, 0, zEt, "#232323", swT * 1.3);
    }

    scene.add(new THREE.AmbientLight(0xffffff, 0.95));
    const dl = new THREE.DirectionalLight(0xffffff, 0.65);
    dl.position.set(2, -3, 4);
    scene.add(dl);

    // `camera.up` ÎNAINTE de OrbitControls: constructorul lui îngheață axa de
    // orbitare din up-ul de atunci, altfel rotirea nu urmează mouse-ul
    camera.up.set(0, 0, 1);
    camera.position.set(L / 2 - L * 0.3, -Math.max(3.0, L * 1.05), h * 0.6);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(L / 2, 0, -h / 2);
    // distanța reală o pune încadrarea, după ce scena e gata (panourile lungi
    // ieșeau din cadru pe lateral)
    incadreaza(camera, controls, scene);

    // randare LA CERERE (nu buclă rAF): scena e statică, iar pane-urile
    // ascunse opresc oricum rAF
    const render = () => renderer.render(scene, camera);
    controls.addEventListener("change", render);
    render();
    return () => {
      controls.removeEventListener("change", render);
      controls.dispose();
      gunoi.forEach((x) => x.dispose());
      renderer.dispose();
      el.removeChild(renderer.domElement);
    };
  }, [p]);

  return <div ref={mount} style={{ width: "100%", height: 380 }} />;
}
